import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { humanise, inr } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, useToast } from '../components/ui';

interface SlotPlan {
  id: string;
  code: string;
  label: string;
  slots: number;
  durationDays: number;
  pricePaise: number;
  gstPercent: string;
  isActive: boolean;
  sortOrder: number;
}
interface BoostPlan {
  id: string;
  code: string;
  label: string;
  days: number;
  pricePaise: number;
  gstPercent: string;
  isActive: boolean;
}
interface SubPlan {
  id: string;
  code: string;
  label: string;
  role: string;
  durationDays: number;
  pricePaise: number;
  gstPercent: string;
  isActive: boolean;
  sortOrder: number;
}

type Editing =
  | { kind: 'slot'; row?: SlotPlan }
  | { kind: 'boost'; row?: BoostPlan }
  | { kind: 'highlight'; row?: BoostPlan }
  | { kind: 'sub'; row?: SubPlan }
  | null;

/** v2 §16/§17 pricing, and the provider/agent plans OQ-V2-8 has not priced. */
export function PlansPage() {
  const [editing, setEditing] = useState<Editing>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const slots = useQuery({ queryKey: ['slot-plans'], queryFn: () => api.get<SlotPlan[]>('/slots/plans') });
  const boosts = useQuery({ queryKey: ['boost-plans'], queryFn: () => api.get<BoostPlan[]>('/boosts/plans') });
  const highlights = useQuery({ queryKey: ['highlight-plans'], queryFn: () => api.get<BoostPlan[]>('/highlights/plans') });
  const subs = useQuery({
    queryKey: ['sub-plans-admin'],
    // The public endpoint hides inactive plans; admin needs to see the unpriced
    // ones precisely because they are the thing that needs attention.
    queryFn: () => api.get<SubPlan[]>('/subscriptions/plans/all'),
  });

  const save = useMutation({
    mutationFn: async ({ kind, body }: { kind: 'slot' | 'boost' | 'highlight' | 'sub'; body: Record<string, unknown> }) => {
      const path =
        kind === 'slot' ? '/slots/plans' : kind === 'boost' ? '/boosts/plans' : kind === 'highlight' ? '/highlights/plans' : '/subscriptions/plans';
      return api.post(path, body);
    },
    onSuccess: () => {
      toast('Pricing saved', 'success');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['slot-plans'] });
      qc.invalidateQueries({ queryKey: ['boost-plans'] });
      qc.invalidateQueries({ queryKey: ['highlight-plans'] });
      qc.invalidateQueries({ queryKey: ['sub-plans-admin'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  // Now that inactive plans are listed, an empty list no longer means
  // "nothing is priced" — it means there are no plans at all. Count the ones
  // that cannot actually be bought instead.
  const unpriced = (subs.data ?? []).filter((p) => !p.isActive || p.pricePaise <= 0);

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      <Notice tone="info" title="Prices are GST-exclusive">
        The figures below are what the customer is charged before GST, exactly as v2 §16 states. GST is added at
        checkout and the split is stored on each payment.
      </Notice>

      {unpriced.length > 0 && (
        <Notice tone="warn" title={`${unpriced.length} subscription plan${unpriced.length === 1 ? '' : 's'} cannot be bought`}>
          {unpriced.map((p) => p.code).join(', ')} {unpriced.length === 1 ? 'is' : 'are'} inactive or priced at
          zero, so the API answers <span className="cell-mono">PLAN_PRICE_UNSET</span> rather than charging an invented
          amount. A partner membership runs for a year and then has to be renewed — until
          <span className="cell-mono"> AGENT_YEARLY</span> has a price, no renewal can be collected. Edit it below to
          set one.
        </Notice>
      )}

      <Card flush>
        <CardHead
          title="Listing slot packs"
          sub="v2 §16"
          action={
            <Button variant="primary" size="sm" onClick={() => setEditing({ kind: 'slot' })}>
              + New pack
            </Button>
          }
        />
        {slots.isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 90 }} />
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Label</th>
                  <th style={{ textAlign: 'right' }}>Slots</th>
                  <th style={{ textAlign: 'right' }}>Days</th>
                  <th style={{ textAlign: 'right' }}>Price</th>
                  <th style={{ textAlign: 'right' }}>GST</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {slots.data?.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-mono">{p.code}</td>
                    <td className="cell-strong">{p.label}</td>
                    <td className="cell-num">{p.slots}</td>
                    <td className="cell-num">{p.durationDays}</td>
                    <td className="cell-num cell-strong">{inr(p.pricePaise)}</td>
                    <td className="cell-num">{p.gstPercent}%</td>
                    <td>
                      <Badge tone={p.isActive ? 'green' : 'neutral'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Button size="sm" onClick={() => setEditing({ kind: 'slot', row: p })}>
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card flush>
        <CardHead
          title="Boost plans"
          sub="v2 §17 — boosted listings show first in the feed"
          action={
            <Button variant="primary" size="sm" onClick={() => setEditing({ kind: 'boost' })}>
              + New boost
            </Button>
          }
        />
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Code</th>
                <th>Label</th>
                <th style={{ textAlign: 'right' }}>Days</th>
                <th style={{ textAlign: 'right' }}>Price</th>
                <th style={{ textAlign: 'right' }}>GST</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {boosts.data?.map((p) => (
                <tr key={p.id}>
                  <td className="cell-mono">{p.code}</td>
                  <td className="cell-strong">{p.label}</td>
                  <td className="cell-num">{p.days}</td>
                  <td className="cell-num cell-strong">{inr(p.pricePaise)}</td>
                  <td className="cell-num">{p.gstPercent}%</td>
                  <td>
                    <Badge tone={p.isActive ? 'green' : 'neutral'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td>
                    <div className="cell-actions">
                      <Button size="sm" onClick={() => setEditing({ kind: 'boost', row: p })}>
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card flush>
        <CardHead
          title="Highlight plans"
          sub="Highlight Ads on Home — a separate service; never shown in Boosted Ads"
          action={
            <Button variant="primary" size="sm" onClick={() => setEditing({ kind: 'highlight' })}>
              + New highlight
            </Button>
          }
        />
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Code</th>
                <th>Label</th>
                <th style={{ textAlign: 'right' }}>Days</th>
                <th style={{ textAlign: 'right' }}>Price</th>
                <th style={{ textAlign: 'right' }}>GST</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {highlights.data?.map((p) => (
                <tr key={p.id}>
                  <td className="cell-mono">{p.code}</td>
                  <td className="cell-strong">{p.label}</td>
                  <td className="cell-num">{p.days}</td>
                  <td className="cell-num cell-strong">{inr(p.pricePaise)}</td>
                  <td className="cell-num">{p.gstPercent}%</td>
                  <td>
                    <Badge tone={p.isActive ? 'green' : 'neutral'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td>
                    <div className="cell-actions">
                      <Button size="sm" onClick={() => setEditing({ kind: 'highlight', row: p })}>
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card flush>
        <CardHead
          title="Provider and partner subscriptions"
          sub="FR-SP-01 and FR-AGT-05"
          action={
            <Button variant="primary" size="sm" onClick={() => setEditing({ kind: 'sub' })}>
              + New plan
            </Button>
          }
        />
        {subs.data?.length ? (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Label</th>
                  <th>Role</th>
                  <th style={{ textAlign: 'right' }}>Days</th>
                  <th style={{ textAlign: 'right' }}>Price</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {subs.data.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-mono">{p.code}</td>
                    <td className="cell-strong">{p.label}</td>
                    <td>{humanise(p.role)}</td>
                    <td className="cell-num">{p.durationDays}</td>
                    <td className="cell-num cell-strong">{inr(p.pricePaise)}</td>
                    <td>
                      {p.isActive ? (
                        <Badge tone="green">Active</Badge>
                      ) : (
                        // Inactive and zero-priced are the same fault in
                        // practice: subscribe() refuses either way.
                        <Badge tone="amber">{p.pricePaise > 0 ? 'Inactive' : 'No price set'}</Badge>
                      )}
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Button size="sm" onClick={() => setEditing({ kind: 'sub', row: p })}>
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty
            icon="⛁"
            title="No priced subscription plans"
            body="The plan shapes exist but are inactive with a zero price. Add one here with a real price to make it purchasable."
          />
        )}
      </Card>

      {editing && <PlanEditor editing={editing} onClose={() => setEditing(null)} onSave={save.mutate} saving={save.isPending} />}
    </div>
  );
}

function PlanEditor({
  editing,
  onClose,
  onSave,
  saving,
}: {
  editing: NonNullable<Editing>;
  onClose: () => void;
  onSave: (v: { kind: 'slot' | 'boost' | 'highlight' | 'sub'; body: Record<string, unknown> }) => void;
  saving: boolean;
}) {
  const row = editing.row as Record<string, unknown> | undefined;
  const [form, setForm] = useState<Record<string, string>>({
    code: (row?.code as string) ?? '',
    label: (row?.label as string) ?? '',
    slots: String(row?.slots ?? 1),
    days: String(row?.days ?? 3),
    durationDays: String(row?.durationDays ?? 30),
    // Shown in rupees; converted to paise on save, since that is what the API
    // takes and what avoids float rounding on the way in.
    priceRupees: row?.pricePaise ? String((row.pricePaise as number) / 100) : '',
    gstPercent: (row?.gstPercent as string) ?? '18',
    role: (row?.role as string) ?? 'SERVICE_PROVIDER',
    isActive: row?.isActive === false ? 'false' : 'true',
  });

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  function submit() {
    const pricePaise = Math.round(Number(form.priceRupees) * 100);
    const common = {
      code: form.code.trim().toUpperCase(),
      label: form.label.trim(),
      pricePaise,
      gstPercent: Number(form.gstPercent),
      isActive: form.isActive === 'true',
    };
    const body =
      editing.kind === 'slot'
        ? { ...common, slots: Number(form.slots), durationDays: Number(form.durationDays) }
        : editing.kind === 'boost' || editing.kind === 'highlight'
          ? { ...common, days: Number(form.days) }
          : { ...common, role: form.role, durationDays: Number(form.durationDays) };
    onSave({ kind: editing.kind, body });
  }

  const title =
    editing.kind === 'slot'
      ? 'Slot pack'
      : editing.kind === 'boost'
        ? 'Boost plan'
        : editing.kind === 'highlight'
          ? 'Highlight plan'
          : 'Subscription plan';

  return (
    <Modal
      title={row ? `Edit ${title.toLowerCase()}` : `New ${title.toLowerCase()}`}
      sub="Prices are GST-exclusive and entered in rupees."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!form.code.trim() || !form.label.trim() || !form.priceRupees}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Code" hint="Stable identifier, e.g. SLOT_5_3M. Reusing one updates that plan.">
          <Input value={form.code} onChange={(e) => set('code', e.target.value)} disabled={Boolean(row)} />
        </Field>
        <Field label="Label">
          <Input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="5 slots · 3 months" />
        </Field>

        {editing.kind === 'slot' && (
          <div className="row">
            <Field label="Slots">
              <Input type="number" min={1} value={form.slots} onChange={(e) => set('slots', e.target.value)} />
            </Field>
            <Field label="Duration (days)">
              <Input type="number" min={1} value={form.durationDays} onChange={(e) => set('durationDays', e.target.value)} />
            </Field>
          </div>
        )}

        {editing.kind === 'boost' && (
          <Field label="Days promoted">
            <Input type="number" min={1} value={form.days} onChange={(e) => set('days', e.target.value)} />
          </Field>
        )}

        {editing.kind === 'highlight' && (
          <Field label="Days highlighted (the plan's validity)">
            <Input type="number" min={1} value={form.days} onChange={(e) => set('days', e.target.value)} />
          </Field>
        )}

        {editing.kind === 'sub' && (
          <div className="row">
            <Field label="Role">
              <Select
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                options={[
                  { value: 'SERVICE_PROVIDER', label: 'Service provider' },
                  { value: 'AGENT', label: 'Partner' },
                ]}
              />
            </Field>
            <Field label="Duration (days)">
              <Input type="number" min={1} value={form.durationDays} onChange={(e) => set('durationDays', e.target.value)} />
            </Field>
          </div>
        )}

        <div className="row">
          <Field label="Price (₹, before GST)">
            <Input type="number" min={0} step="0.01" value={form.priceRupees} onChange={(e) => set('priceRupees', e.target.value)} />
          </Field>
          <Field label="GST %">
            <Input type="number" min={0} step="0.01" value={form.gstPercent} onChange={(e) => set('gstPercent', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select
              value={form.isActive}
              onChange={(e) => set('isActive', e.target.value)}
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
            />
          </Field>
        </div>

        {form.priceRupees && (
          <Notice tone="info">
            Customer pays{' '}
            <strong>
              {inr(Math.round(Number(form.priceRupees) * 100 * (1 + Number(form.gstPercent) / 100)), { paise: true })}
            </strong>{' '}
            including {form.gstPercent}% GST.
          </Notice>
        )}
      </div>
    </Modal>
  );
}
